import { useEffect, useRef, useState } from 'react';
import { columnOriginX, margins, round } from '../../../utils/etiqueta/geometry';
import ElementoEtiqueta from './ElementoEtiqueta';

const PX_PER_MM = 4;
const RULER = 18;

export default function EtiquetaCanvas({ ed, zoom, setZoom, grid, snap, data, issueIds, mostrarMacros = false }) {
  const { model } = ed;
  const roll = model.roll;
  const scale = PX_PER_MM * zoom;
  const wrap = useRef(null);
  const drag = useRef(null);
  const [, force] = useState(0);
  const step = snap ? 0.5 : 0.1;
  const snapV = (v) => round(Math.round(v / step) * step, 2);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setZoom(Math.min(6, Math.max(0.5, +(zoom * (e.deltaY < 0 ? 1.1 : 0.9)).toFixed(2))));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoom, setZoom]);

  const onDown = (e, el, mode) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    ed.select(el.id);
    drag.current = { id: el.id, mode, sx: e.clientX, sy: e.clientY, start: model, el, moved: false };
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.sx) / scale;
    const dy = (e.clientY - d.sy) / scale;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) < 0.3) return;
    d.moved = true;
    const patch = d.mode === 'move'
      ? { x: snapV(d.el.x + dx), y: snapV(d.el.y + dy) }
      : { w: Math.max(0.5, snapV(d.el.w + dx)), h: Math.max(0.3, snapV(d.el.h + dy)) };
    d.latest = {
      ...d.start,
      elements: d.start.elements.map((item) => (item.id === d.id ? { ...item, ...patch } : item)),
    };
    ed.setModel(d.latest, false);
    force((n) => n + 1);
  };
  const onUp = () => {
    const d = drag.current;
    drag.current = null;
    if (d?.moved && d.latest) {
      ed.setModel(d.start, false);
      ed.setModel(d.latest, true);
    }
  };

  const { left } = margins(roll);
  const W = roll.rollWidth * scale;
  const H = roll.labelHeight * scale;
  const ticks = (len) => Array.from({ length: Math.floor(len) + 1 }, (_, i) => i);

  return (
    <div
      ref={wrap}
      onPointerDown={() => ed.select(null)}
      style={{ position: 'relative', height: '100%', minHeight: 420, overflow: 'auto', background: '#f3f0e8' }}
    >
      <div style={{ display: 'inline-block', padding: 40, minWidth: '100%' }}>
        <div style={{ position: 'relative', margin: '0 auto', width: W + RULER, height: H + RULER + 40 }}>
          <div style={{ position: 'absolute', top: 0, left: RULER, width: W, height: RULER, borderBottom: '1px solid #ccc', background: '#fff' }}>
            {ticks(roll.rollWidth).map((i) => (
              <div key={i} style={{ position: 'absolute', bottom: 0, left: i * scale, height: i % 10 === 0 ? 10 : i % 5 === 0 ? 6 : 3, borderLeft: '1px solid #888' }}>
                {i % 10 === 0 && <span style={{ position: 'absolute', top: -10, left: 2, fontSize: 8, fontFamily: 'monospace' }}>{i}</span>}
              </div>
            ))}
          </div>
          <div style={{ position: 'absolute', left: 0, top: RULER, width: RULER, height: H, borderRight: '1px solid #ccc', background: '#fff' }}>
            {ticks(roll.labelHeight).map((i) => (
              <div key={i} style={{ position: 'absolute', right: 0, top: i * scale, width: i % 10 === 0 ? 10 : i % 5 === 0 ? 6 : 3, borderTop: '1px solid #888' }}>
                {i % 10 === 0 && (
                  <span style={{ position: 'absolute', left: -2, top: 0, transform: 'translateX(-100%)', fontSize: 8, fontFamily: 'monospace' }}>{i}</span>
                )}
              </div>
            ))}
          </div>
          <div style={{ position: 'absolute', left: RULER, top: RULER, width: W, height: H, background: '#e7e2d8' }}>
            {Array.from({ length: roll.columns }, (_, c) => {
              const x = columnOriginX(roll, c) * scale;
              const main = c === 0;
              return (
                <div
                  key={c}
                  style={{
                    position: 'absolute',
                    left: x,
                    top: 0,
                    width: roll.labelWidth * scale,
                    height: H,
                    background: '#fff',
                    boxShadow: main ? '0 1px 4px rgba(0,0,0,.18)' : 'none',
                    outline: main ? '1px solid rgba(21,101,192,.45)' : 'none',
                    opacity: main ? 1 : 0.55,
                    backgroundImage: grid && main
                      ? 'linear-gradient(to right, rgba(0,0,0,.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,.08) 1px, transparent 1px)'
                      : undefined,
                    backgroundSize: grid && main ? `${scale}px ${scale}px` : undefined,
                  }}
                >
                  {!main && (
                    <span style={{ position: 'absolute', right: 4, top: 2, fontSize: 9, fontFamily: 'monospace', color: '#666' }}>
                      col {c + 1}
                    </span>
                  )}
                  {model.elements.map((el) => {
                    const sel = main && ed.selectedId === el.id;
                    return (
                      <div
                        key={el.id}
                        onPointerDown={main ? (e) => onDown(e, el, 'move') : undefined}
                        onPointerMove={main ? onMove : undefined}
                        onPointerUp={main ? onUp : undefined}
                        style={{
                          position: 'absolute',
                          left: el.x * scale,
                          top: el.y * scale,
                          width: Math.max(1, el.w * scale),
                          height: Math.max(1, el.h * scale),
                          touchAction: 'none',
                          userSelect: 'none',
                          cursor: main ? 'move' : 'default',
                          outline: sel ? '2px solid #1565c0' : issueIds.has(el.id) ? '1px solid #ed6c02' : '1px dashed transparent',
                        }}
                      >
                        <ElementoEtiqueta el={el} scale={scale} data={data} mostrarMacros={mostrarMacros} />
                        {sel && (
                          <div
                            onPointerDown={(e) => onDown(e, el, 'resize')}
                            onPointerMove={onMove}
                            onPointerUp={onUp}
                            style={{
                              position: 'absolute',
                              right: -6,
                              bottom: -6,
                              width: 12,
                              height: 12,
                              background: '#1565c0',
                              border: '2px solid #fff',
                              borderRadius: 2,
                              cursor: 'nwse-resize',
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          <p style={{ position: 'absolute', left: RULER, top: RULER + H + 8, margin: 0, fontSize: 11, fontFamily: 'monospace', color: '#666' }}>
            Bobina {roll.rollWidth} mm · etiqueta {roll.labelWidth}×{roll.labelHeight} mm · margem esquerda {left} mm
          </p>
        </div>
      </div>
    </div>
  );
}
